import { Injectable, inject } from '@angular/core';
import { take } from 'rxjs';
import { messaging } from '../../../configs/firebase.config';
import { environment } from '../../../environments/environment';
import { ApiHttpService } from './api-http.service';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

const SK_BLOCKED = 'martin-bbq-fcm-warn-blocked';
const SK_TOKEN = 'martin-bbq-fcm-warn-token';
const SK_UNSUPPORTED = 'martin-bbq-fcm-warn-unsupported';

@Injectable({
  providedIn: 'root',
})
export class FcmDeviceRegistrationService {
  private readonly http = inject(ApiHttpService);
  private readonly auth = inject(AuthService);
  private readonly toast = inject(ToastService);

  /**
   * Resolves a web FCM token before login when supported. Prompts for notification
   * permission if still in the default state. No toasts — failures are silent so
   * login is never blocked; post-login tryRegisterWebPush can retry.
   */
  async getWebPushTokenForLogin(): Promise<string | undefined> {
    if (typeof window === 'undefined' || !messaging || !('Notification' in window)) {
      return undefined;
    }
    let perm = Notification.permission;
    if (perm === 'denied') {
      return undefined;
    }
    if (perm === 'default') {
      try {
        perm = await Notification.requestPermission();
      } catch {
        return undefined;
      }
    }
    if (perm !== 'granted') {
      return undefined;
    }
    return this.getVapidToken();
  }

  /** Registers this browser for FCM when a dashboard session is active. */
  tryRegisterWebPush(): void {
    if (typeof window === 'undefined' || !messaging) {
      return;
    }
    if (!this.auth.getAuthToken()?.access_token) {
      return;
    }
    if (!('Notification' in window)) {
      this.warnOnce(
        SK_UNSUPPORTED,
        'Notifications not supported',
        'This browser cannot receive push alerts. Use a current Chrome, Edge, or Firefox.',
      );
      return;
    }

    const afterGranted = () => {
      void this.getVapidToken().then((token) => {
        if (!token) {
          this.warnOnce(
            SK_TOKEN,
            'Push not active',
            'Could not get a device token. Check that notifications are allowed for this site, then reload.',
          );
          return;
        }
        this.http
          .post<{ token: string; platform: string }, unknown>('notification/register-device', {
            token,
            platform: 'web',
          })
          .pipe(take(1))
          .subscribe({
            error: () => {
              this.toast.showWarn('Device registration failed', 'Could not save push token to the server.');
            },
          });
      });
    };

    const perm = Notification.permission;
    if (perm === 'denied') {
      this.warnPermissionBlocked();
      return;
    }
    if (perm === 'granted') {
      afterGranted();
      return;
    }

    Notification.requestPermission()
      .then((next) => {
        if (next === 'granted') {
          afterGranted();
        } else {
          this.warnPermissionBlocked();
        }
      })
      .catch(() => {
        this.warnPermissionBlocked();
      });
  }

  private getVapidToken(): Promise<string | undefined> {
    if (!messaging) return Promise.resolve(undefined);
    return messaging
      .getToken({ vapidKey: environment.firebaseConfig.vapidKey })
      .then((t) => t || undefined)
      .catch(() => undefined);
  }

  private warnPermissionBlocked(): void {
    this.warnOnce(
      SK_BLOCKED,
      'Notifications blocked or denied',
      'You will not get order or store alerts until notifications are allowed. In Chrome: click the tune/site settings icon next to the URL → Permissions → Notifications → Allow. Then reload this page.',
    );
  }

  private warnOnce(key: string, summary: string, detail: string): void {
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch {
      /* private mode */
    }
    this.toast.showWarn(summary, detail);
  }
}
